import { Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map, tap } from 'rxjs';

import { getCookie } from '@core/application/use-cases/cookies/get-cookie.use-case';
import { Category } from '@core/domain/models/category.models';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { SetProductIdsPageGrid } from '@pages/category/modules/category-grid/store/category-grid.actions';
import { getLastCategoryState } from '@pages/category/store/category-page.selectors';
import { CategoryState } from '@pages/category/store/category-page.state';
import { getProductsViewed } from '@pages/product/public_api';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';
import { getRecommendationsByPlacement } from '@shared/product/related-products/application/use-cases/recommendations-by-placement.uc';
import { RecommendationsByPlacementParamsGroup } from '@shared/product/related-products/domain/models/recommendations-by-placement.model';
import {
    RelatedProductsRepositoryInterface,
} from '@shared/product/related-products/domain/related-products-repository.interface';

const getLastCategory = (store: Store): Category => {
    const lastCategoryState = store.selectSignal(getLastCategoryState)();
    return lastCategoryState?.category;
};

export const getLastProductIndexToSort = (
    lastProductIndexToSort: number,
    lastProductIndexToSortFromState: number,
): number => {
    return lastProductIndexToSortFromState === null || lastProductIndexToSort < lastProductIndexToSortFromState
        ? lastProductIndexToSort
        : lastProductIndexToSortFromState;
};

/**
 * Función encargada de ordenar un array de product ids en función del producto previamente visualizado y sus productos recomendados
 *
 * @param recommendationsProductIds number[]
 * @param categoryProductIds number[]
 * @param productsViewed Product[]
 * @param limit number
 *
 * @returns number[]
 */
export const orderProductIdsByRecommendations = (
    recommendationsProductIds: number[],
    categoryProductIds: number[],
    productsViewed: ProductPublicModels.PublicProduct[],
    limit: number,
): { lastProductIndexToSort: number; productsId: number[] } => {
    const lastProductViewed = productsViewed?.length ? productsViewed[productsViewed.length - 1] : undefined;

    // If no valid last viewed product or the last viewed product is not in the category, return all category products
    if (!(lastProductViewed && categoryProductIds.includes(+lastProductViewed.id))) {
        return { lastProductIndexToSort: undefined, productsId: categoryProductIds };
    }

    const lastViewedIndex = categoryProductIds.indexOf(+lastProductViewed.id);
    const beforeLastViewed = categoryProductIds.slice(0, lastViewedIndex + 1);
    const afterLastViewed = categoryProductIds.slice(lastViewedIndex + 1);

    // Filter recommended products that are in the category and apply the limit
    const recommendedIdsInCategory = recommendationsProductIds
        .filter(id => afterLastViewed.includes(id))
        .slice(0, limit); // Limit the number of recommended products

    // Filter non-recommended products based on the limited recommended list
    const nonRecommendedIds = afterLastViewed.filter(id => !recommendedIdsInCategory.includes(id));

    // Return products before the last viewed, limited recommended products, and the remaining non-recommended ones
    return {
        lastProductIndexToSort: lastViewedIndex,
        productsId: [...beforeLastViewed, ...recommendedIdsInCategory, ...nonRecommendedIds],
    };
};

export const loadRecommendationsAndSort$ = (
    store: Store,
    document: Document,
    relatedProductsRepository: RelatedProductsRepositoryInterface,
) => (
    categoryId: string,

): Observable<{ categoryId: string; lastState: CategoryState; error?: boolean }> => {
    const lastCategoryState$$ = store.selectSignal(getLastCategoryState);
    const lastCategoryState = lastCategoryState$$();
    const productsViewed$$ = store.selectSignal(getProductsViewed);
    const productsViewed = productsViewed$$();
    const lastSection$$ = store.selectSignal(getLastSectionSelector);
    const lastSection = lastSection$$();
    const limit$$: Signal<number> = store.selectSignal(getCmsConfigValue('recommendations_oyml_productLimit'));
    const limit = limit$$();

    const sessionId = getCookie(document.cookie, 'ITXSESSIONID');
    const googleAnalyticsCookie = getCookie(document.cookie, '_ga');

    const getSectionName: Record<string, number> = {
        WOMEN: 1,
        MEN: 2,
    };

    const paramsGroup: RecommendationsByPlacementParamsGroup = {
        placeName: 'PRODUCT_GRID',
        type: 'other_you_may_like',
        sessionId,
        section: getSectionName[lastSection],
        campaign: undefined,
        limit: undefined,
        baseProducts: productsViewed.slice(-1),
        categoryId,
        googleAnalyticsCookie,
    };

    return getRecommendationsByPlacement(
        relatedProductsRepository,
        store,
        paramsGroup,
    ).pipe(
        // eslint-disable-next-line camelcase
        map(recommendations => recommendations.map(({ product_id }) => +product_id)),
        map(recommendationsProductIds => {
            return orderProductIdsByRecommendations(
                recommendationsProductIds,
                lastCategoryState?.productsId,
                productsViewed,
                limit,
            );
        }),
        tap(({ productsId }) => {
            store.dispatch(SetProductIdsPageGrid({ productsId }));
        }),
        map(({ productsId, lastProductIndexToSort }) => ({
            categoryId,
            lastState: {
                ...lastCategoryState,
                defaultOrders: {
                    ...lastCategoryState?.defaultOrders,
                    recommended: productsId,
                },
                productsId,
                lastProductIndexToSort: getLastProductIndexToSort(
                    lastProductIndexToSort,
                    lastCategoryState.lastProductIndexToSort,
                ),
            },
        })),
    );
};

export const isRecommendationFetchNeeded = (store: Store) => (
    categoryId: string,
): boolean => {
    const recommendationsEnabled = store.selectSignal(getCmsConfigValue('recommendations_oyml_category_enabled'))();
    const lastCategory = getLastCategory(store);
    const productsViewed = store.selectSignal(getProductsViewed)();
    return recommendationsEnabled
        && lastCategory?.id === categoryId
        && lastCategory?.attachments?.find(attachment => attachment.name === 'oyml_personalization_sort')
        && !!productsViewed?.length;
};
